<template>
	<section class="homepage-maps-section">
		<div class="row">
			<div class="highlights-nav-index">
				<div
					v-for="item in items"
					:key="item.ID"
					class="item"
					:data-id="`#huid_${item.ID}`"
					@click="openInfowindow($event)"
				>
					<div class="icon-wrapper">
						<font-awesome-icon :icon="`fal fa-${item.icon}`" size="1x" />
					</div>
					<div class="header-wrapper">
						<span>{{ item.header }}</span>
						<strong>{{ item.distanceWalk }}</strong>
					</div>
				</div>
			</div>
		</div>

		<div class="maps">
			<div class="map-overlay">
				<h2>{{ header }}</h2>
			</div>
			<div id="gmap" ref="gmap" />
		</div>
	</section>
</template>

<script setup>
import { Loader } from '@googlemaps/js-api-loader';
import GoogleMaps from '@becurioushq/googlemaps';

const props = defineProps({
	items: { type: Array, default: () => [] },
	header: { type: String, default: '' },
});

const config = useRuntimeConfig();
const gmap = ref(null);
const activeCategory = ref(null);
const allHighlights = ref([]);

let map = null;
const mapData = {};

const mapConfig = {
	zoom: 14,
	center: { lat: 52.0799021, lng: 4.305498 },
	styles: [
		{ featureType: 'poi', elementType: 'all', stylers: [{ visibility: 'off' }] },
		{
			featureType: 'road.highway',
			elementType: 'all',
			stylers: [{ visibility: 'off' }, { weight: 0.6 }, { saturation: -85 }, { lightness: 61 }],
		},
		{ featureType: 'road.highway', elementType: 'geometry', stylers: [{ visibility: 'on' }] },
		{ featureType: 'road.arterial', elementType: 'all', stylers: [{ visibility: 'off' }] },
		{ featureType: 'road.local', elementType: 'all', stylers: [{ visibility: 'on' }] },
	],
	draggable: true,
	mapTypeControl: false,
	zoomControl: false,
	streetViewControl: false,
	scrollwheel: false,
};

watchEffect(() => {
	allHighlights.value = props.items;
});

onMounted(async () => {
	const google = await new Loader({
		apiKey: config.public.maps.apiKey,
	}).load();
	map = new GoogleMaps(google, gmap.value, mapConfig);
	initializeMap();

	updateMarkers();
});

const initializeMap = () => {
	map.setMarkerIcon({
		src: '/images/marker.png',
		width: 30,
		height: 42,
		anchorX: 15,
		anchorY: 21,
	});

	map.addInfoWindow(
		map.addMarker(Object.values(mapConfig.center), 'Arq Paleis Hotel'),
		'<div class="highlights-window"><div class="highlights-content"><h3>Arq Paleis Hotel</h3>' +
			'<p class="address">Molenstraat 26<br>2513 BL Den Haag</p></div></div>',
	);
};

const updateMarkers = (id) => {
	if (!map) {
		return;
	}

	if (id === activeCategory.value) {
		activeCategory.value = null;
	} else {
		activeCategory.value = id;
	}

	map.setMarkerIcon({
		src: '/images/marker-item.png',
		width: 30,
		height: 42,
		anchorX: 15,
		anchorY: 21,
	});

	map.hideMarker('allcategory');

	allHighlights.value.forEach((highlight) => {
		const marker = map.addMarker(
			[highlight.location.latitude, highlight.location.longitude],
			highlight.header,
			`cat_${highlight.category}`,
		);

		const infowindow = map.addInfoWindow(marker, getInfowindowContent(highlight));

		mapData[`#huid_${highlight.ID}`] = [infowindow, marker];
	});

	if (activeCategory.value) {
		map.autoCenter();
	}
};

const openInfowindow = (event) => {
	const ID = event.target.dataset.id;

	if (ID) {
		const currentMapData = mapData[event.target.dataset.id];
		map.openInfowindow(currentMapData[0], currentMapData[1]);
	}
};

const getInfowindowContent = (highlight) => {
	let html = '<div class="highlights-window"> <div class="highlights-content">';

	html += '<h3>' + highlight.header + '</h3>';

	html += `<p class="address">
			${highlight.location.address}<br>${highlight.location.postalCode} ${highlight.location.city}
			</p>`;

	if (highlight.phonenumber) {
		html += `<p class="phone">${highlight.phonenumber}</p>`;
	}

	if (highlight.distance_public) {
		html += `<p class="directions public"><span class="icon-directions"></span>
				<span>${highlight.distancePublic}</span>
				</p>`;
	}

	if (highlight.distance_walk) {
		html += `<p class="directions walk"><span class="icon-directions"></span>
				<span>${highlight.distanceWalk}</span>
				</p>`;
	}

	if (highlight.description) {
		html += `<p class="description">
				<span>${highlight.description}</span>
				</p>`;
	}

	if (highlight.url) {
		html += `<p class="website">
				<a href="${highlight.url}" target="_blank" rel="noopener noreferrer">Website</a>
				</p>`;
	}

	html += '</div></div>';

	return html;
};
</script>

<style lang="scss" scoped>
.maps {
	padding: 0 0 70px;
	position: relative;

	&::after {
		display: block;
		content: '';
		position: absolute;
		inset: auto 0 0;
		height: 270px;
		z-index: 2;
		background: linear-gradient(0deg, #eee7e4 25%, rgb(238 231 228 / 0%) 100%);
	}
}

.row {
	position: absolute;
	inset: auto 0 0;
	z-index: 3;
}

#gmap {
	min-height: 600px;
}

.map-overlay {
	position: absolute;
	inset: 0 0 104px;
	z-index: 4;
	background: rgb(238 231 228 / 60%);
	display: flex;
	justify-content: center;
	align-items: center;
	text-align: center;
	font-style: italic;
	transition: all 0.5s ease-in-out;
}

.homepage-maps-section {
	position: relative;

	&:hover {
		.map-overlay {
			z-index: -1;
			opacity: 0;
		}
	}
}

.highlights-nav-index {
	width: 100%;
	display: flex;
	flex-flow: row nowrap;
	justify-content: center;
	padding: 30px 0;

	.category,
	.item {
		display: flex;
		flex-flow: row nowrap;
	}

	.item {
		justify-content: flex-start;
		align-items: center;
		padding: 5px 20px;
		cursor: pointer;
		z-index: 1;
		position: relative;
		border-right: 2px solid;

		&:last-child {
			border: none;
		}

		&::before {
			content: '';
			position: absolute;
			inset: 0;
		}
	}

	.icon-wrapper {
		width: 50px;
		text-align: left;
		font-size: 30px;
	}
}

.header-wrapper {
	display: flex;
	flex-flow: column wrap;
	justify-content: flex-start;
	align-items: flex-start;
	text-transform: uppercase;
	font-size: 13px;

	span {
		margin: 0 0 5px;
	}
}

@media (max-width: 800px) {
	.maps {
		padding: 0 0 200px;
	}

	#gmap {
		min-height: 400px;
	}

	.highlights-nav-index {
		flex-flow: row wrap;

		.item {
			width: 50%;
			padding: 7px 10px;
			border-right: none;

			.icon-wrapper {
				font-size: 22px;
				width: 35px;
			}

			.header-wrapper {
				font-size: 11px;
			}

			&:nth-child(2) {
				border: none;
			}
		}
	}

	.map-overlay {
		inset: 0 0 200px;
	}
}
</style>
